import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Calendar, Clock, PoundSterling, FileText, CreditCard } from "lucide-react";
import { ActSelector } from "@/components/acts/act-selector";
import { FeeComparisonIndicator } from "@/components/bookings/fee-comparison-indicator";
import type { Booking, ExternalEvent, ActWithParticipants } from "@shared/schema";

interface BookingFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId: number;
  booking?: Booking;
  eventId?: number;
  actId?: number;
}

export function BookingFormModal({ 
  open, 
  onOpenChange, 
  organisationId, 
  booking,
  eventId,
  actId 
}: BookingFormModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedAct, setSelectedAct] = useState<ActWithParticipants | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    actId: actId || 0,
    externalEventId: eventId || 0,
    status: "pending" as "pending" | "confirmed" | "cancelled" | "completed",
    fee: "",
    commissionType: "" as "" | "percentage" | "flat",
    commissionValue: "",
    addCommissionToFee: false,
    hasDeposit: false,
    depositAmount: "",
    depositPaid: false,
    performanceTime: "",
    durationMinutes: "",
    contractSigned: false,
    paymentStatus: "pending" as "pending" | "paid" | "partial" | "refunded",
    invoiceNumber: "",
    notes: "",
  });

  // Handle act selection
  const handleActSelection = (act: ActWithParticipants | null) => {
    setSelectedAct(act);
    setFormData(prev => ({ ...prev, actId: act?.id || 0 }));
  };

  // Fetch external events
  const { data: externalEvents = [] } = useQuery<ExternalEvent[]>({
    queryKey: [`/api/external-events?organisationId=${organisationId}`],
    queryFn: async () => {
      const response = await fetch(`/api/external-events?organisationId=${organisationId}`);
      if (!response.ok) throw new Error("Failed to fetch events");
      return response.json();
    },
    enabled: open,
  });

  // Load existing booking data if editing
  useEffect(() => {
    if (booking) {
      setFormData({
        actId: booking.actId,
        externalEventId: booking.externalEventId || 0,
        status: booking.status as "pending" | "confirmed" | "cancelled" | "completed",
        fee: booking.fee ? (booking.fee / 100).toString() : "",
        commissionType: (booking.commissionType || "") as "" | "percentage" | "flat",
        commissionValue: booking.commissionValue 
          ? booking.commissionType === "percentage" 
            ? (booking.commissionValue / 100).toString() // Convert from basis points
            : (booking.commissionValue / 100).toString() // Convert from cents
          : "",
        addCommissionToFee: false,
        hasDeposit: booking.hasDeposit || false,
        depositAmount: booking.depositAmount ? (booking.depositAmount / 100).toString() : "",
        depositPaid: booking.depositPaid || false,
        performanceTime: booking.performanceTime ? new Date(booking.performanceTime).toISOString().slice(0, 16) : "",
        durationMinutes: booking.durationMinutes?.toString() || "",
        contractSigned: booking.contractSigned || false,
        paymentStatus: booking.paymentStatus as "pending" | "paid" | "partial" | "refunded",
        invoiceNumber: booking.invoiceNumber || "",
        notes: booking.notes || "",
      });
    } else {
      // Reset form for new booking with provided defaults
      setFormData({
        actId: actId || 0,
        externalEventId: eventId || 0,
        status: "pending",
        fee: "",
        commissionType: "",
        commissionValue: "",
        addCommissionToFee: false,
        hasDeposit: false,
        depositAmount: "",
        depositPaid: false,
        performanceTime: "",
        durationMinutes: "",
        contractSigned: false,
        paymentStatus: "pending",
        invoiceNumber: "",
        notes: "",
      });
    }
  }, [booking, eventId, actId]); // Removed externalEvents from dependencies

  // Set event date when eventId is provided and event data is loaded
  useEffect(() => {
    if (!booking && eventId && externalEvents.length > 0) {
      const event = externalEvents.find(e => e.id === eventId);
      if (event) {
        setFormData(prev => ({
          ...prev,
          performanceTime: new Date(event.date).toISOString().slice(0, 16),
        }));
      }
    }
  }, [booking, eventId, externalEvents]);


  // Create/update booking mutation
  const mutation = useMutation({
    mutationFn: async (data: any) => {
      const url = booking 
        ? `/api/bookings/${booking.id}`
        : `/api/bookings?organisationId=${organisationId}`;
      
      const response = await fetch(url, {
        method: booking ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to save booking");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/bookings`] });
      toast({
        title: booking ? "Booking Updated" : "Booking Created",
        description: booking ? "Booking has been updated." : "New booking has been created.",
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    if (!formData.actId || !formData.externalEventId) {
      toast({
        title: "Missing Required Fields",
        description: "Please select both an act and an event.",
        variant: "destructive",
      });
      return;
    }

    // Calculate the final fee including commission if "Add to Fee" is checked
    let finalFee = formData.fee ? parseFloat(formData.fee) : 0;
    if (formData.commissionType === "flat" && formData.addCommissionToFee && formData.commissionValue) {
      finalFee += parseFloat(formData.commissionValue);
    }

    const data: any = {
      actId: formData.actId,
      externalEventId: formData.externalEventId,
      status: formData.status,
      fee: finalFee ? Math.round(finalFee * 100) : undefined,
      commissionType: formData.commissionType || undefined,
      commissionValue: formData.commissionValue && formData.commissionType
        ? formData.commissionType === "percentage"
          ? Math.round(parseFloat(formData.commissionValue) * 100) // Convert to basis points (10% = 1000)
          : Math.round(parseFloat(formData.commissionValue) * 100) // Convert to cents
        : undefined,
      hasDeposit: formData.hasDeposit,
      depositAmount: formData.hasDeposit && formData.depositAmount 
        ? Math.round(parseFloat(formData.depositAmount) * 100) // Convert to cents
        : undefined,
      depositPaid: formData.depositPaid,
      performanceTime: formData.performanceTime ? new Date(formData.performanceTime).toISOString() : undefined,
      durationMinutes: formData.durationMinutes ? parseInt(formData.durationMinutes) : undefined,
      contractSigned: formData.contractSigned,
      paymentStatus: formData.paymentStatus,
      invoiceNumber: formData.invoiceNumber || undefined,
      notes: formData.notes || undefined,
    };

    mutation.mutate(data);
  };

  const selectedEvent = externalEvents.find(e => e.id === formData.externalEventId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{booking ? "Edit Booking" : "Create Booking"}</DialogTitle>
          <DialogDescription>
            {booking ? "Update the booking details." : "Book an act for an external event."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Act Selection */}
          <ActSelector
            organisationId={organisationId}
            selectedActId={actId || formData.actId}
            onSelectAct={handleActSelection}
            label="Act"
            placeholder="Search for acts..."
            required={true}
          />

          {/* Event Selection */}
          <div>
            <Label htmlFor="event">
              Event <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.externalEventId.toString()}
              onValueChange={(value) => setFormData(prev => ({ ...prev, externalEventId: parseInt(value) }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an event" />
              </SelectTrigger>
              <SelectContent>
                {externalEvents
                  .filter(e => e.status !== "cancelled" && e.status !== "completed")
                  .map((event) => (
                    <SelectItem key={event.id} value={event.id.toString()}>
                      {event.name} - {new Date(event.date).toLocaleDateString()}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Booking Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="status">Booking Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value: any) => setFormData(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="fee">
                <PoundSterling className="inline h-4 w-4 mr-1" />
                Performance Fee
              </Label>
              <Input
                id="fee"
                type="number"
                step="0.01"
                value={formData.fee}
                onChange={(e) => setFormData(prev => ({ ...prev, fee: e.target.value }))}
                placeholder="1500.00"
              />
              <FeeComparisonIndicator
                actualFee={formData.fee ? parseFloat(formData.fee) * 100 : null}
                minimumFee={selectedAct?.minimumFee}
                preferredFee={selectedAct?.preferredFee}
              />
            </div>
          </div>

          {/* Commission Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="commissionType">Commission Type</Label>
              <Select
                value={formData.commissionType}
                onValueChange={(value: any) => setFormData(prev => ({ ...prev, commissionType: value, addCommissionToFee: false }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select commission type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage of Fee</SelectItem>
                  <SelectItem value="flat">Flat Amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="commissionValue">
                Commission {formData.commissionType === "percentage" ? "(%)" : "(£)"}
              </Label>
              <Input
                id="commissionValue"
                type="number"
                step={formData.commissionType === "percentage" ? "0.01" : "1"}
                value={formData.commissionValue}
                onChange={(e) => setFormData(prev => ({ ...prev, commissionValue: e.target.value }))}
                placeholder={formData.commissionType === "percentage" ? "10" : "150.00"}
                disabled={!formData.commissionType}
              />
              {formData.fee && formData.commissionValue && formData.commissionType && (
                <p className="text-xs text-slate-600 mt-1">
                  Commission: £{
                    formData.commissionType === "percentage"
                      ? ((parseFloat(formData.fee) * parseFloat(formData.commissionValue)) / 100).toFixed(2)
                      : parseFloat(formData.commissionValue).toFixed(2)
                  }
                  {formData.commissionType === "percentage" && 
                    ` | Net: £${(parseFloat(formData.fee) - (parseFloat(formData.fee) * parseFloat(formData.commissionValue)) / 100).toFixed(2)}`
                  }
                  {formData.commissionType === "flat" && !formData.addCommissionToFee &&
                    ` | Net: £${(parseFloat(formData.fee) - parseFloat(formData.commissionValue)).toFixed(2)}`
                  }
                  {formData.commissionType === "flat" && formData.addCommissionToFee &&
                    ` | Total Fee: £${(parseFloat(formData.fee) + parseFloat(formData.commissionValue)).toFixed(2)}`
                  }
                </p>
              )}
            </div>
          </div>
          
          {/* Add to Fee Toggle for Flat Commission */}
          {formData.commissionType === "flat" && formData.commissionValue && (
            <div className="flex items-center gap-2 bg-blue-50 rounded-lg p-3 border border-blue-200">
              <input
                type="checkbox"
                id="addCommissionToFee"
                checked={formData.addCommissionToFee}
                onChange={(e) => setFormData(prev => ({ ...prev, addCommissionToFee: e.target.checked }))}
                className="h-4 w-4"
              />
              <Label htmlFor="addCommissionToFee" className="cursor-pointer text-sm">
                Add to Fee (Client pays £{(parseFloat(formData.fee || "0") + parseFloat(formData.commissionValue)).toFixed(2)} total)
              </Label>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="performanceTime">
                <Calendar className="inline h-4 w-4 mr-1" />
                Performance Time
              </Label>
              <Input
                id="performanceTime"
                type="datetime-local"
                value={formData.performanceTime}
                onChange={(e) => setFormData(prev => ({ ...prev, performanceTime: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="duration">
                <Clock className="inline h-4 w-4 mr-1" />
                Duration (minutes)
              </Label>
              <Input
                id="duration"
                type="number"
                value={formData.durationMinutes}
                onChange={(e) => setFormData(prev => ({ ...prev, durationMinutes: e.target.value }))}
                placeholder="60"
              />
            </div>
          </div>

          {/* Payment Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="paymentStatus">
                <CreditCard className="inline h-4 w-4 mr-1" />
                Payment Status
              </Label>
              <Select
                value={formData.paymentStatus}
                onValueChange={(value: any) => setFormData(prev => ({ ...prev, paymentStatus: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="invoiceNumber">
                <FileText className="inline h-4 w-4 mr-1" />
                Invoice Number
              </Label>
              <Input
                id="invoiceNumber"
                value={formData.invoiceNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                placeholder="INV-2024-001"
              />
            </div>
          </div>

          {/* Deposit Section */}
          <div className="space-y-3 border-t pt-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="hasDeposit" className="text-base font-medium">
                Deposit Required
              </Label>
              <Switch
                id="hasDeposit"
                checked={formData.hasDeposit}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, hasDeposit: checked }))}
              />
            </div>
            
            {formData.hasDeposit && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 ml-4">
                <div>
                  <Label htmlFor="depositAmount">
                    <PoundSterling className="inline h-4 w-4 mr-1" />
                    Deposit Amount
                  </Label>
                  <Input
                    id="depositAmount"
                    type="number"
                    step="0.01"
                    value={formData.depositAmount}
                    onChange={(e) => setFormData(prev => ({ ...prev, depositAmount: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <Switch
                    id="depositPaid"
                    checked={formData.depositPaid}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, depositPaid: checked }))}
                  />
                  <Label htmlFor="depositPaid" className="cursor-pointer">
                    Deposit Paid
                  </Label>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="contractSigned"
              checked={formData.contractSigned}
              onChange={(e) => setFormData(prev => ({ ...prev, contractSigned: e.target.checked }))}
              className="h-4 w-4"
            />
            <Label htmlFor="contractSigned" className="cursor-pointer">
              Contract Signed
            </Label>
          </div>

          <div>
            <Label htmlFor="notes">Internal Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
              placeholder="Any additional notes about this booking..."
            />
          </div>
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={mutation.isPending}
            className="w-full sm:w-auto"
          >
            {mutation.isPending ? "Saving..." : booking ? "Update Booking" : "Create Booking"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}